import type { ThemeMode } from "@/config/themes/types";

// ─── API ─────────────────────────────────────────────────────────

/** Header name required on every request to the Olive API. */
export const TENANT_HEADER = "tenant";

// ─── Auth & session ──────────────────────────────────────────────

/** Auth realm: tenant UI (per-tenant Cognito) vs admin UI (Olive staff Cognito). */
export const AUTH_REALM = {
  TENANT: "tenant",
  ADMIN: "admin",
} as const;

/** Per-realm Cognito token cookie names (never shared between tenant and admin). */
export const COGNITO_REALM_COOKIE = {
  [AUTH_REALM.TENANT]: {
    ID: "olive_tenant_id_token",
    REFRESH: "olive_tenant_refresh_token",
  },
  [AUTH_REALM.ADMIN]: {
    ID: "olive_admin_id_token",
    REFRESH: "olive_admin_refresh_token",
  },
} as const;

/**
 * Refresh-token cookie lifetime, matching `RefreshTokenValidity` on the Cognito app client
 * (backend `COGNITO_REFRESH_TOKEN_VALIDITY`, 120 minutes). Must never exceed it: too short forces a
 * clean re-login, too long leaves the app reporting a live session with a discarded token.
 */
export const REFRESH_TOKEN_MAX_AGE_SECONDS = 120 * 60;

/** Per-realm OAuth PKCE / state sessionStorage keys. */
export const COGNITO_REALM_OAUTH = {
  [AUTH_REALM.TENANT]: {
    PKCE_VERIFIER: "olive_pkce_verifier_tenant",
    AUTH_STATE: "olive_auth_state_tenant",
  },
  [AUTH_REALM.ADMIN]: {
    PKCE_VERIFIER: "olive_pkce_verifier_admin",
    AUTH_STATE: "olive_auth_state_admin",
  },
} as const;

/** sessionStorage key for cached SSO login-enabled flag used at login redirect. */
export const SSO_LOGIN_ENABLED_CACHE_KEY = "olive_sso_login_enabled";

/** sessionStorage flag set after logout to gate re-entry to Cognito with prompt=login. */
export const POST_LOGOUT_SESSION_KEY = "olive_post_logout";

/** sessionStorage flag marking that `/callback` already restarted login once, to bound retries. */
export const AUTH_RESTART_ATTEMPTED_KEY = "olive_auth_restart_attempted";

/** SSO configuration state values returned by `GET /v1/sso-configurations/`. */
export const SSO_STATE = {
  CONFIGURED: "configured",
  ENABLED: "enabled",
} as const;

/** LLM providers a tenant may register an API key for (`GET /v1/llm-api-keys/`). */
export const LLM_PROVIDER = {
  OPENAI: "openai",
  ANTHROPIC: "anthropic",
} as const;

/** What a catalogue model is for; groups the model picker in Settings > LLM. */
export const LLM_MODEL_KIND = {
  CHAT: "chat",
  EMBEDDING: "embedding",
  IMAGE: "image",
} as const;

/**
 * Placeholder shown in place of a stored LLM API key.
 *
 * A literal, not a truncation: the backend never returns the key or any fragment of it, so there is
 * nothing to derive a mask from. The length is arbitrary and deliberately does not match any real
 * provider key length, so it cannot be read as a hint about the stored value.
 */
export const LLM_KEY_MASK = "\u2022".repeat(32);

/** Cognito SAML IdP name provisioned by the backend. */
export const COGNITO_SAML_IDENTITY_PROVIDER = "default";

/** BroadcastChannel name used to sync session-expiry logout across open tabs. */
export const AUTH_SESSION_BROADCAST_CHANNEL = "olive_auth_session";

/** Message posted on `AUTH_SESSION_BROADCAST_CHANNEL` when a tab detects session expiry. */
export const AUTH_SESSION_EXPIRED_MESSAGE = "session-expired";

// ─── Appearance ──────────────────────────────────────────────────

/** Theme mode values. */
export const THEME_MODE = {
  DARK: "dark" as ThemeMode,
  LIGHT: "light" as ThemeMode,
} as const;

/** localStorage key for the user's theme appearance preference. */
export const APPEARANCE_STORAGE_KEY = "appearance";

/**
 * localStorage key for the tenant's last-known brand colours, painted before the branding request
 * returns so a branded tenant does not flash Olive's green on every load. Per origin, and every
 * tenant is its own subdomain, so one tenant's colours never reach another's.
 */
export const BRAND_COLORS_STORAGE_KEY = "brandColors";

// ─── Notifications ─────────────────────────────────────────────────

/** Auto-hide duration for global snackbar notifications (ms). */
export const NOTIFICATION_AUTO_HIDE_MS = 6000;

/** Variant values for the global notification snackbar. */
export const NOTIFICATION_VARIANT = {
  SUCCESS: "success",
  ERROR: "error",
} as const;

// ─── Branding ────────────────────────────────────────────────────

/** Largest branding image upload the backend accepts (bytes); mirrors `MAX_IMAGE_BYTES`. */
export const BRANDING_IMAGE_MAX_BYTES = 512 * 1024;

/** Longest brand name the backend accepts; mirrors `BRAND_NAME_MAX_LENGTH`. */
export const BRANDING_NAME_MAX_LENGTH = 100;

/**
 * The seeds Olive's own theme corresponds to: its light-mode primary and its sidebar green. Shown
 * in the colour pickers until a tenant chooses its own. Olive's theme itself is hand-tuned rather
 * than derived from these, so leaving the colours unset keeps it exactly as it is.
 */
export const DEFAULT_BRAND_COLORS = {
  PRIMARY: "#0c2d16",
  SECONDARY: "#79a979",
} as const;

/** Image formats the backend accepts, for the file picker's `accept` and the client-side check. */
export const BRANDING_IMAGE_CONTENT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/svg+xml",
] as const;

/**
 * Cognito's limits for the sign-in page images, which are narrower than the app's. They mirror
 * olive-backend's `login_page_branding`; Cognito does not document them.
 */
export const BRANDING_SIGN_IN_LOGO_MAX_BYTES = 500 * 1024;
export const BRANDING_SIGN_IN_FAVICON_MAX_BYTES = 100 * 1024;
/** The widest sign-in logo Cognito takes, as width over height. It takes nothing taller than square. */
export const BRANDING_SIGN_IN_LOGO_MAX_RATIO = 4;
/** The largest first image Cognito takes in an ICO tab icon, in pixels a side. */
export const BRANDING_SIGN_IN_FAVICON_ICO_MAX_PX = 192;

/** Formats Cognito takes, for the sign-in file pickers' `accept`; the bytes are checked as well. */
export const BRANDING_SIGN_IN_LOGO_ACCEPT = "image/png,image/jpeg,image/svg+xml";
export const BRANDING_SIGN_IN_FAVICON_ACCEPT =
  "image/svg+xml,image/x-icon,image/vnd.microsoft.icon,.ico";

// ─── UI layout ───────────────────────────────────────────────────

/** Brand logo asset path (public URL). */
export const OLIVE_LOGO_SRC = "/brand/logo.svg";

/** `id` of the `<link rel="icon">` in `index.html`, which the tenant's icon replaces when set. */
export const FAVICON_ELEMENT_ID = "app-favicon";

/** How long a copy button shows its "copied" tick before reverting (ms). */
export const COPY_FEEDBACK_MS = 1600;

/** Sidebar width when expanded (px). */
export const SIDEBAR_EXPANDED_WIDTH_PX = 220;

/** Sidebar width when collapsed (px). */
export const SIDEBAR_COLLAPSED_WIDTH_PX = 56;

/** The two tabs splitting the add/edit widget dialogs: the query and its result, then the widgets. */
export const WIDGET_DIALOG_TAB = {
  QUERY: "query",
  WIDGETS: "widgets",
} as const;

export type WidgetDialogTabKey = (typeof WIDGET_DIALOG_TAB)[keyof typeof WIDGET_DIALOG_TAB];

/** Width of the left panel holding the Query & data / Widget settings tabs in the widget dialogs (px). */
export const WIDGET_DIALOG_TAB_PANEL_WIDTH_PX = 200;

/** Width of the label column (Widget type, Description, Width, Tab) in a widget card's header (px). */
export const WIDGET_CARD_FIELD_LABEL_WIDTH_PX = 180;

/** Max height of a searchable multi-select's list before it scrolls (px); about six options. */
export const SEARCHABLE_PICKER_LISTBOX_MAX_HEIGHT_PX = 360;

/** Max height of the scrollable nugget picker list in the add-nugget-feed dialog (px). */
export const NUGGET_FEED_PICKER_MAX_HEIGHT_PX = 320;

/** Summary lines shown per row in the nugget picker before clamping to an ellipsis. */
export const NUGGET_FEED_PICKER_SUMMARY_MAX_LINES = 3;

/** Day cell size in the tab-filter date range calendar (px); keeps two months inside the popover. */
export const DATE_RANGE_CALENDAR_DAY_SIZE_PX = 36;

/** Widest an inline document citation chip may grow before its file name is ellipsized (px).
 * The full name is always in the chip's tooltip and in the source list. */
export const DOCUMENT_CITATION_CHIP_MAX_WIDTH_PX = 220;

/**
 * Ceiling on the markdown a domain can carry into its semantic generation prompt.
 *
 * Mirrors MAX_GENERATION_INSTRUCTIONS_CHARS in the backend's domain schema, which rejects
 * anything longer. Checked here too so a mis-dropped file fails immediately, against the
 * editor, rather than as a 422 after a round trip.
 */
export const MAX_GENERATION_INSTRUCTIONS_CHARS = 60_000;

/**
 * Fixed height of an app card in the App Studio and Published Apps grids (px).
 *
 * Pinned rather than left to the content so every card in the grid is the same size whether or
 * not its app carries a description. Sized to the tallest layout the card can hold: the icon
 * block, a one-line title and a two-line description, plus its padding.
 */
export const APP_CARD_HEIGHT_PX = 200;

/** Description lines an app card shows before clamping to an ellipsis. */
export const APP_CARD_DESCRIPTION_MAX_LINES = 2;

/** Width of a table card on the domain canvas (px). Also the card's React Flow node width. */
export const CANVAS_TABLE_CARD_WIDTH_PX = 280;

/** Height of a table card's header block on the domain canvas (px). */
export const CANVAS_TABLE_CARD_HEADER_HEIGHT_PX = 56;

/** Height of one column row on a canvas table card (px). */
export const CANVAS_TABLE_CARD_ROW_HEIGHT_PX = 42;

/** Horizontal gap between auto-placed table cards on the domain canvas (px). */
export const CANVAS_TABLE_CARD_GAP_X_PX = 140;

/** Vertical gap between auto-placed table cards on the domain canvas (px). */
export const CANVAS_TABLE_CARD_GAP_Y_PX = 48;

/**
 * Column rows a canvas table card shows before the rest are folded away.
 *
 * A forty-column view renders ~1800px tall — taller than the viewport, so both ends of a join
 * can never be on screen at once. A table with this many columns or fewer has nothing to fold,
 * so it opens whole.
 */
export const CANVAS_TABLE_COMPACT_ROWS = 10;

/** Table cards per row before the domain canvas auto-layout wraps to a new band. */
export const CANVAS_TABLE_CARDS_PER_ROW = 4;

/**
 * Height a column of canvas cards grows to before it wraps into another column (px).
 *
 * A star schema puts every fact in one column and every dimension in the next, so a
 * domain with a dozen of each lays out barely a thousand pixels wide and nearly five
 * thousand tall — technically tidy, but zoomed to fit it is a thread of unreadable
 * cards. About eight full-height cards, so it only bites on the columns that are
 * genuinely too long; measured on a twenty-one table domain, wrapping there shortened
 * both the average and the longest relationship line as well as squaring the diagram
 * up, while wrapping harder (a target of 2400) made every line longer again by pushing
 * joined tables further apart.
 */
export const CANVAS_COLUMN_TARGET_HEIGHT_PX = 4000;

/**
 * How long the canvas takes to zoom back onto a freshly arranged diagram (ms).
 *
 * Animated rather than instant on purpose: rearranging moves every card at once, and a
 * jump cut loses which card you were looking at.
 */
export const CANVAS_ARRANGE_FIT_DURATION_MS = 400;

/** Secondary sidebar width when expanded (px). */
export const SECONDARY_SIDEBAR_EXPANDED_WIDTH_PX = 300;

/** Secondary sidebar width when collapsed (px). */
export const SECONDARY_SIDEBAR_COLLAPSED_WIDTH_PX = 56;

/**
 * Width of a drill-down rendered as a side panel, as a percentage of the viewport.
 *
 * A layout constant rather than a per-drill-down setting: the point of the side panel is that the
 * widget behind it stays readable, and one stored width per drill-down would drift between them
 * for no benefit. Wide enough for a detail record, narrow enough to leave the grid usable.
 */
export const DRILLDOWN_DRAWER_WIDTH_PERCENT = 40;

/** Page container padding (px). */
export const PAGE_PADDING = {
  DEFAULT: {
    LEFT_PX: 24,
    RIGHT_PX: 24,
    Y_PX: 24,
  },
  DOMAIN_DETAIL_PAGE: {
    LEFT_PX: 0,
    RIGHT_PX: 24,
    Y_PX: 16,
  },
  APP_BUILD_PAGE: {
    LEFT_PX: 0,
    RIGHT_PX: 24,
    Y_PX: 16,
  },
  HOME_PAGE: {
    LEFT_PX: 0,
    RIGHT_PX: 0,
    Y_PX: 0,
  },
} as const;

export const NUGGETS_BRACKET_SECTION_LAYOUT = {
  BRACKET_HEIGHT_PX: 70,
  BRACKET_BORDER_WIDTH_PX: 2,
  BRACKET_BORDER_RADIUS_PX: 12,
  BRACKET_OUTSET_PX: 0,
  LABEL_OFFSET_TOP_PX: -14,
  LABEL_OFFSET_LEFT_PX: 16,
  PADDING_PX: 24,
  PADDING_TOP_PX: 32,
  COLLAPSED_MARGIN_BOTTOM_PX: 24,
  EXPANDED_MARGIN_BOTTOM_PX: 32,
} as const;

/** Top offset (px) for the global snackbar so it clears header actions and the user avatar. */
export const NOTIFICATION_SNACKBAR_TOP_OFFSET_PX =
  PAGE_PADDING.DEFAULT.Y_PX + 40 + 8;

/** Stacking order for the floating user avatar overlay. */
export const LAYOUT_AVATAR_Z_INDEX = 10;

/** Horizontal space reserved so header actions do not sit under the avatar.
 *  Covers the whole floating cluster, not just the avatar: the theme toggle sits to its left
 *  (52px track + 8px gap + 40px avatar, plus breathing room). */
export const LAYOUT_AVATAR_RESERVED_WIDTH_PX = 116;

/** Clarification chat drawer width (px) by breakpoint. */
export const CLARIFICATION_CHAT_DRAWER_WIDTH = {
  XS_PX: 400,
  SM_PX: 520,
  MD_PX: 600,
  LG_PX: 720,
} as const;

/** App builder chat drawer width (px) by breakpoint — 75% of clarification chat. */
export const APP_BUILDER_CHAT_DRAWER_WIDTH = {
  XS_PX: CLARIFICATION_CHAT_DRAWER_WIDTH.XS_PX * 0.75,
  SM_PX: CLARIFICATION_CHAT_DRAWER_WIDTH.SM_PX * 0.75,
  MD_PX: CLARIFICATION_CHAT_DRAWER_WIDTH.MD_PX * 0.75,
  LG_PX: CLARIFICATION_CHAT_DRAWER_WIDTH.LG_PX * 0.75,
} as const;

/** Placeholder email shown until real user profile is wired. */
export const DUMMY_USER_EMAIL = "user@example.com";

/**
 * Rows per page in a paginated App Studio table widget. No page-size selector is offered.
 *
 * Purely client-side: the server sends a *block* of up to a thousand rows and the browser slices
 * it into pages of this size, so changing it needs no backend change.
 */
export const APP_TABLE_PAGE_SIZE = 10;

/** Measured per-row height (px) of an App Studio table at MUI `size="small"` `stickyHeader`: the previous 320px panel exactly fit a header row + 9 data rows (320 / 10 row-units). */
export const APP_TABLE_ROW_HEIGHT_PX = 32;

/** Height of a paginated table's `TablePagination` footer (px) — MUI's own toolbar `minHeight` at default density. A chart card also reserves this height but renders nothing in it, so a chart and a table in the same row stay the same total height. */
export const APP_TABLE_PAGINATION_FOOTER_HEIGHT_PX = 52;

/**
 * Fixed height for chart cards and table panels so they align in mixed grids (px).
 *
 * Sized to fit a table's header row plus APP_TABLE_PAGE_SIZE (10) data rows with no scrolling, at
 * APP_TABLE_ROW_HEIGHT_PX per row-unit, plus one spare row-unit.
 *
 * The spare unit is what keeps the last data row whole. A row-unit is what a row's *content* is
 * pinned to, but a rendered row is that plus its 1px cell border-bottom, and a header cell's text
 * sits taller again -- so a panel of exactly 1 + 10 row-units came out a dozen-odd pixels short of
 * the rows it was meant to hold. A paginated grid suppresses its vertical scrollbar (nothing is
 * supposed to be out of reach), so the shortfall showed up as a last row clipped in half with no
 * way to scroll to it.
 */
export const CHART_TABLE_PANEL_HEIGHT_PX =
  APP_TABLE_ROW_HEIGHT_PX * (2 + APP_TABLE_PAGE_SIZE);

/** Gap between items in the app builder grid (px); also reused as the gap between two stacked KPI cards. */
export const APP_ITEMS_GRID_GAP_PX = 24;

/** Height of the drag-handle + edit/delete control row shown above every chart, table, and KPI item (px). Identical across item kinds so a stacked KPI pair lines up with a chart/table row. */
export const APP_ITEM_TOOLBAR_ROW_HEIGHT_PX = 40;

/**
 * Fixed height for a KPI metric card's content box (px). Two stacked KPI
 * cards (each under their own toolbar row) plus the gap between them add up
 * to exactly one chart/table item's total height (toolbar + panel), so a KPI
 * pair can sit flush alongside a chart or table of the same column span.
 */
export const KPI_CARD_HEIGHT_PX = Math.round(
  (CHART_TABLE_PANEL_HEIGHT_PX -
    APP_ITEM_TOOLBAR_ROW_HEIGHT_PX -
    APP_ITEMS_GRID_GAP_PX) /
    2,
);

/** Fixed height for a chart card's title/description header, or a table card's description slot (px). Identical across both widget kinds — and across the published view and the App Studio builder, which lay their cards out the same way — so their total card heights always match. */
export const PUBLISHED_ITEM_HEADER_HEIGHT_PX = 56;

/** Vertical padding (top + bottom) around a chart/table card's content area (px), in both the published view and the App Studio builder. */
export const PUBLISHED_ITEM_CONTENT_PADDING_PX = 24;

/** 1px top + 1px bottom card border (border-box), reserved in the total so the summary's last line is never clipped by the card's own border. */
export const PUBLISHED_CARD_BORDER_PX = 2;

/** Height of ChartPanel's internal controls row — the chart-type / download / fullscreen icon buttons rendered above the chart canvas (px). A published chart is this row + a gap + the fixed canvas, so it is taller than a bare table. */
export const PUBLISHED_CHART_CONTROLS_ROW_HEIGHT_PX = 40;

/** Vertical gap between ChartPanel's controls row and its canvas (px). */
export const PUBLISHED_CHART_CONTROLS_GAP_PX = 8;

/**
 * Reserved height for a widget's content region (px): the chart's controls row +
 * gap + the fixed chart/table canvas + the table pagination footer. Both chart and
 * table cards reserve this taller (chart-driven) region so their totals match; a
 * table simply top-aligns its shorter canvas inside it, and a chart leaves the
 * footer's height blank since it has no pagination controls of its own. Neither the
 * chart nor the table canvas is shrunk. Shared by the published view and the App
 * Studio builder.
 */
export const PUBLISHED_ITEM_CONTENT_HEIGHT_PX =
  PUBLISHED_CHART_CONTROLS_ROW_HEIGHT_PX +
  PUBLISHED_CHART_CONTROLS_GAP_PX +
  CHART_TABLE_PANEL_HEIGHT_PX +
  APP_TABLE_PAGINATION_FOOTER_HEIGHT_PX;

/** Number of "AI summary" body lines shown before truncating with an ellipsis; the full text is available via a tooltip on hover. */
export const PUBLISHED_SUMMARY_MAX_LINES = 3;

/** Explicit line height applied to the published item summary's label and body text (px), so the reserved summary block height below is exact rather than dependent on the typography theme. */
export const PUBLISHED_SUMMARY_LINE_HEIGHT_PX = 20;

/** Space above the summary block, separating it from the chart/table content above it (px). */
export const PUBLISHED_SUMMARY_TOP_GAP_PX = 12;

/**
 * Fixed height for a widget's "AI summary" heading + up to
 * PUBLISHED_SUMMARY_MAX_LINES of body text (px). Always reserved — even when
 * the summary is short or missing — so every chart/table card is the same
 * height regardless of summary length, in the builder as in the published view.
 */
export const PUBLISHED_SUMMARY_BLOCK_HEIGHT_PX =
  PUBLISHED_SUMMARY_TOP_GAP_PX +
  PUBLISHED_SUMMARY_LINE_HEIGHT_PX +
  PUBLISHED_SUMMARY_LINE_HEIGHT_PX * PUBLISHED_SUMMARY_MAX_LINES;

/**
 * Fixed total height of a published chart or table card (px): card border +
 * header/description slot + content padding + the content region (chart
 * controls row + gap + canvas) + the summary block. Every published widget
 * card renders at this height so rows never leave a gap around shorter/taller
 * neighbors, and the full 3-line summary is always visible below the canvas.
 */
export const PUBLISHED_CARD_HEIGHT_PX =
  PUBLISHED_CARD_BORDER_PX +
  PUBLISHED_ITEM_HEADER_HEIGHT_PX +
  PUBLISHED_ITEM_CONTENT_PADDING_PX +
  PUBLISHED_ITEM_CONTENT_HEIGHT_PX +
  PUBLISHED_SUMMARY_BLOCK_HEIGHT_PX;

/**
 * Fixed height for a published KPI card (px) — half of PUBLISHED_CARD_HEIGHT_PX
 * once the gap between a stacked pair is subtracted, so two stacked published
 * KPI cards line up flush beside one published chart/table card. Distinct from
 * BUILDER_KPI_CARD_HEIGHT_PX because a published card also reserves a header
 * and content padding the builder's cards don't.
 */
export const PUBLISHED_KPI_CARD_HEIGHT_PX = Math.round(
  (PUBLISHED_CARD_HEIGHT_PX - APP_ITEMS_GRID_GAP_PX) / 2,
);

/**
 * A published chart/table card on a compact row — one where every chart and table has its AI
 * summary hidden (px): the full card less the summary block nobody on the row needs reserved.
 */
export const PUBLISHED_COMPACT_CARD_HEIGHT_PX =
  PUBLISHED_CARD_HEIGHT_PX - PUBLISHED_SUMMARY_BLOCK_HEIGHT_PX;

/** A published KPI card on a compact row (px), so a stacked pair stays flush with the shorter card. */
export const PUBLISHED_COMPACT_KPI_CARD_HEIGHT_PX = Math.round(
  (PUBLISHED_COMPACT_CARD_HEIGHT_PX - APP_ITEMS_GRID_GAP_PX) / 2,
);

/**
 * A published KPI card on a row with no chart or table (px). Nothing there sets a height to line
 * up with, so the card takes a standard stat-tile height instead of half a chart's: room around
 * its large-type value and label without leaving a tall, mostly empty box. A notch taller than
 * BUILDER_STANDALONE_KPI_CARD_HEIGHT_PX for that larger type, as on every other row.
 */
export const PUBLISHED_STANDALONE_KPI_CARD_HEIGHT_PX = 180;

/** Longest text after a KPI change chip's number (e.g. "vs last week"); the server's limit too. */
export const KPI_CHANGE_LABEL_MAX_LENGTH = 40;

/**
 * Where a KPI card's native colour pickers start while no colour is set. Only the picker's
 * starting point -- nothing is saved until the user picks, and an unset colour renders with the
 * theme's own surface and text tokens.
 */
export const KPI_STYLE_PICKER_FALLBACK_COLORS = {
  CARD: "#ffffff",
  TEXT: "#1a1a1a",
  /** The change chip's rise and fall, near the theme's own green and red. */
  INCREASE: "#2e7d32",
  DECREASE: "#d32f2f",
} as const;

/**
 * A KPI card's font sizes (rem) per size preset, for its value and its name. COMPACT is the
 * builder's type scale and LARGE the published view's (`largeContent`). `md` equals the card's
 * standard type, so a card with no size set looks as it always has, and `xl` still fits value
 * and name inside the shortest card (KPI_CARD_HEIGHT_PX, PUBLISHED_STANDALONE_KPI_CARD_HEIGHT_PX).
 */
export const KPI_CARD_FONT_SIZE_REM = {
  VALUE: {
    COMPACT: { sm: 1.5, md: 2.125, lg: 2.75, xl: 3.5 },
    LARGE: { sm: 2.125, md: 3, lg: 3.75, xl: 4.5 },
  },
  LABEL: {
    COMPACT: { sm: 0.75, md: 0.875, lg: 1, xl: 1.25 },
    LARGE: { sm: 1, md: 1.25, lg: 1.5, xl: 1.75 },
  },
  /**
   * The change chip's number and the text after it, the same in both views. Its arrow is a third
   * larger. `md` is the chip's standard size.
   */
  CHANGE: { sm: 0.625, md: 0.75, lg: 0.875, xl: 1 },
} as const;

/**
 * Fixed height of an App Studio builder chart/table card (px): the title/description
 * header slot, the content padding, the reserved content region (chart controls row +
 * gap + canvas + pagination footer) and the AI summary block below it.
 *
 * Composed from exactly the same parts as PUBLISHED_CARD_HEIGHT_PX, so a widget looks
 * the same in the builder as it does once published. It is a *fixed* height for the
 * same reason the published card is: a content-sized card makes a chart and a table
 * sharing a row end at different heights -- a chart has no pagination footer, a table
 * has no controls row, and a description is only sometimes there -- which left their
 * summaries visibly out of line. Only the card's own border is absent here, since the
 * builder draws its widgets unframed.
 */
export const BUILDER_CARD_HEIGHT_PX =
  PUBLISHED_ITEM_HEADER_HEIGHT_PX +
  PUBLISHED_ITEM_CONTENT_PADDING_PX +
  PUBLISHED_ITEM_CONTENT_HEIGHT_PX +
  PUBLISHED_SUMMARY_BLOCK_HEIGHT_PX;

/**
 * Total height of an App Studio builder chart/table item (px): the drag/edit toolbar
 * row the builder adds above every widget, plus the card itself. Used to keep a
 * stacked KPI pair aligned with a chart or table of the same column span.
 */
export const BUILDER_ITEM_HEIGHT_PX =
  APP_ITEM_TOOLBAR_ROW_HEIGHT_PX + BUILDER_CARD_HEIGHT_PX;

/**
 * Fixed height for a KPI card's content box in the App Studio builder grid (px).
 * Two stacked KPI cards — each under its own toolbar row, plus the gap between
 * them — add up to exactly one builder chart/table item, so a KPI pair sits
 * flush beside a chart or table of the same column span.
 *
 * Taller than KPI_CARD_HEIGHT_PX because the chart/table it aligns with now
 * carries an AI summary block, while a KPI has no summary of its own.
 * KPI_CARD_HEIGHT_PX stays the default for KPI cards outside that grid (the
 * chat cards panel), which have no summary block to align against.
 */
export const BUILDER_KPI_CARD_HEIGHT_PX = Math.round(
  (BUILDER_ITEM_HEIGHT_PX -
    2 * APP_ITEM_TOOLBAR_ROW_HEIGHT_PX -
    APP_ITEMS_GRID_GAP_PX) /
    2,
);

/** A builder chart/table card on a compact row (px) — see PUBLISHED_COMPACT_CARD_HEIGHT_PX. */
export const BUILDER_COMPACT_CARD_HEIGHT_PX =
  BUILDER_CARD_HEIGHT_PX - PUBLISHED_SUMMARY_BLOCK_HEIGHT_PX;

/** A builder KPI card on a compact row (px), so a stacked pair stays flush with the shorter card. */
export const BUILDER_COMPACT_KPI_CARD_HEIGHT_PX = Math.round(
  (BUILDER_COMPACT_CARD_HEIGHT_PX -
    APP_ITEM_TOOLBAR_ROW_HEIGHT_PX -
    APP_ITEMS_GRID_GAP_PX) /
    2,
);

/**
 * A builder KPI card on a row with no chart or table (px) — see
 * PUBLISHED_STANDALONE_KPI_CARD_HEIGHT_PX. The same tile size the chat cards panel gives a KPI.
 */
export const BUILDER_STANDALONE_KPI_CARD_HEIGHT_PX = 160;

// ─── Async status ────────────────────────────────────────────────

/** Async operation status values used across Redux slices and UI. */
export const STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
} as const;

// ─── Data Hub ────────────────────────────────────────────────────

/** Debounce window (ms) for batching domain entities PATCH calls. */
export const ENTITIES_PATCH_DEBOUNCE_MS = 300;

/**
 * Domain document upload rules. `MAX_BYTES_PER_FILE` mirrors the backend's own ceiling
 * (`MAX_DOCUMENT_BYTES` in `pdf_document_scanner.py`) so an oversized file fails locally instead of
 * after a wasted upload — the backend stays the authority. `MAX_FILES_PER_UPLOAD` bounds one
 * selection only; there is no cap on how many documents a domain may hold in total.
 */
export const DOMAIN_DOCUMENT_UPLOAD = {
  ACCEPTED_MIME_TYPE: "application/pdf",
  ACCEPTED_EXTENSION: ".pdf",
  MAX_BYTES_PER_FILE: 25 * 1024 * 1024,
  MAX_FILES_PER_UPLOAD: 10,
} as const;

/**
 * How often the documents tab re-reads embedding status while a run is in flight. The backend
 * answers this endpoint straight from Bedrock, so each poll is two AWS calls — slow enough to
 * stay cheap, fast enough that a short ingestion still shows progress before it finishes.
 */
export const DOMAIN_DOCUMENT_EMBEDDING_POLL_MS = 5000;

/**
 * How often the Enrichment tab re-reads an in-flight Alation catalog sync.
 *
 * Slower than the document-embedding poll: a sync is minutes of Alation round trips with no
 * per-item progress to show, so the only thing a tick can reveal is that the run finished.
 */
export const ALATION_SYNC_POLL_MS = 8000;

/** Terminal states of an Alation catalog sync, as the backend records them. */
export const ALATION_SYNC_STATUS = {
  RUNNING: "running",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
} as const;

/**
 * Per-document indexing states reported by the Bedrock Knowledge Base. Only the ones the UI
 * treats specially are listed; any other status is displayed verbatim, since Bedrock's
 * vocabulary can grow without this build knowing about it.
 */
export const DOMAIN_DOCUMENT_EMBEDDING_STATUS = {
  INDEXED: "INDEXED",
  PENDING: "PENDING",
  IN_PROGRESS: "IN_PROGRESS",
  STARTING: "STARTING",
  FAILED: "FAILED",
  IGNORED: "IGNORED",
  /** The knowledge base has never seen this file — uploaded but not embedded yet. */
  NOT_FOUND: "NOT_FOUND",
} as const;

/**
 * Classification reference data uploads (taxonomy + labelled example files). JSON rather than PDF,
 * and a separate ceiling from `DOMAIN_DOCUMENT_UPLOAD` because these are data files the classifier
 * reads, not documents to index.
 */
export const CLASSIFICATION_UPLOAD = {
  ACCEPTED_MIME_TYPE: "application/json",
  ACCEPTED_EXTENSION: ".json",
  MAX_BYTES_PER_FILE: 25 * 1024 * 1024,
  MAX_FILES_PER_UPLOAD: 50,
} as const;

/**
 * How often the classification section re-reads embedding state while a run is in flight. The
 * backend answers this from one S3 listing with no object download, so a poll is cheap.
 */
export const CLASSIFICATION_EMBEDDING_POLL_MS = 5000;

/**
 * Whether the stored vectors describe the uploaded example files as they are now. One state for
 * the whole set rather than one per file: every vector lives in a single S3 document, so there is
 * nothing per-file to compare against.
 */
export const CLASSIFICATION_EMBEDDING_STATE = {
  /** No example files uploaded yet, so there is nothing to embed. */
  NO_EXAMPLES: "no_examples",
  /** Examples exist but have never been embedded. */
  NOT_EMBEDDED: "not_embedded",
  /** The stored vectors are at least as new as every example file. */
  EMBEDDED: "embedded",
  /** An example file has been added or replaced since the vectors were written. */
  STALE: "stale",
} as const;

// ─── Scheduled jobs ──────────────────────────────────────────────────

/**
 * Scheduled-job type values (API wire format), matching the backend `JobType` enum.
 *
 * Two kinds of entry live here, and they are not interchangeable:
 *
 * - types Data Hub can **create** (`classification_job`, `copy_job`, `app_snapshot_job`), which
 *   drive the Jobs tab — all three are also gated per tenant by staff;
 * - types Data Hub cannot create but staff can **gate** per tenant
 *   (`sweep_user_learning_insights`), which drive the admin tenant page's Jobs card only.
 *
 * `sweep_user_learning_insights` is the second kind: its definition is seeded when a tenant is
 * provisioned, never authored here, so it must stay out of Data Hub's own job-type lists —
 * `belongsToDomain` already returns false for it, which is what keeps it off the Jobs tab.
 *
 * The backend has others again (`email`, `scheduled_noop`) that are neither authored here nor
 * gated, so they are absent entirely.
 */
export const JOB_TYPE = {
  CLASSIFICATION: "classification_job",
  COPY: "copy_job",
  APP_SNAPSHOT: "app_snapshot_job",
  LEARNING_HISTORY: "sweep_user_learning_insights",
} as const;

export type JobTypeValue = (typeof JOB_TYPE)[keyof typeof JOB_TYPE];

/**
 * Longest static prefix an app snapshot job's name may use, matching the backend's own cap.
 *
 * The cap exists so the assembled name always fits `app_snapshots.name`, which is VARCHAR(255).
 * The generated half is a date and nothing else, so the longest it runs is `"2026-09-16"` plus the
 * single space that joins it — 131 characters in all, leaving slack for a longer format later.
 */
export const SNAPSHOT_NAME_PREFIX_MAX_CHARS = 120;

/**
 * Limits on a classification job's `input` block, mirroring the backend's `ClassificationInput`
 * model so the form refuses what the API would.
 */
export const CLASSIFICATION_INPUT_LIMITS = {
  COLUMN_MAX_CHARS: 255,
  NAME_MAX_CHARS: 100,
  TEXT_FIELDS_MAX: 20,
  LABEL_COLUMNS_MAX: 3,
  INSTRUCTIONS_MAX_CHARS: 4000,
} as const;

/**
 * The batching placeholder a classification query may contain, and the value the backend starts it
 * at. It is not SQL, so a preview substitutes the starting value before sending the statement.
 */
export const CLASSIFICATION_BATCH_PLACEHOLDER = {
  TOKEN: "${END}",
  START_VALUE: "0",
} as const;

/** Most source files a copy job may take per run, matching the backend's own cap. */
export const COPY_JOB_MAX_FILES_PER_RUN = 1000;

/** App publication status values (API wire format), matching the backend `AppStatus` enum. */
export const APP_STATUS = {
  DRAFT: "draft",
  PUBLISHED: "published",
} as const;

/**
 * How long App Studio waits after the last write to a published app's draft before re-checking
 * whether the draft still differs from what is published. One edit is often several writes.
 */
export const APP_DRAFT_STATUS_REFRESH_DELAY_MS = 400;

/**
 * Per-user app access levels (API wire format), matching the backend `AppAccessLevel` enum.
 * `VIEW` reaches the published app only; `MANAGE` also reaches it in App Studio.
 */
export const APP_ACCESS_LEVEL = {
  VIEW: "view",
  MANAGE: "manage",
} as const;

/** Height the share dialog's list of people grows to before it scrolls. */
export const SHARE_ACCESS_PEOPLE_LIST_MAX_HEIGHT_PX = 280;

/** Narrowest the share dialog's per-person tab menu gets, so "All tabs" and its hint fit. */
export const SHARE_ACCESS_TAB_MENU_MIN_WIDTH_PX = 240;

/** Narrowest the share dialog's access menu gets, so each level and its hint fit on one line. */
export const SHARE_ACCESS_LEVEL_MENU_MIN_WIDTH_PX = 260;

/** Width the access and tab dropdowns on each person's row start at, so they line up in columns. */
export const SHARE_ACCESS_ROW_CONTROL_MIN_WIDTH_PX = 116;

/** Widest a person's tab dropdown grows to before a long tab name is cut short. */
export const SHARE_ACCESS_ROW_CONTROL_MAX_WIDTH_PX = 168;

/** Narrowest the share dialog's "Add people" box gets before its controls wrap below it. */
export const SHARE_ACCESS_ADD_PEOPLE_MIN_WIDTH_PX = 200;

/** Size of the initial shown beside each person in the share dialog. */
export const SHARE_ACCESS_AVATAR_SIZE_PX = 32;

// ─── Connections ─────────────────────────────────────────────────────

/** Connection type values (API wire format). */
export const CONNECTION_TYPE = {
  SNOWFLAKES: "SNOWFLAKE",
  CUBE: "CUBE",
  MYSQL: "MYSQL",
  S3: "S3",
} as const;

/** Default MySQL port when not specified in connection config. */
export const MYSQL_DEFAULT_PORT = 3306;

/** Region prefilled into a new S3 connection; the user overrides it when their bucket differs. */
export const S3_DEFAULT_REGION = "us-east-1";

/** Snowflake authentication method values. */
export const SNOWFLAKE_AUTH_METHOD = {
  PRIVATE_KEY: "private_key",
  PASSWORD: "password",
} as const;

/** Cube authentication type values. */
export const CUBE_AUTH_TYPE = {
  OAUTH2_CLIENT_CREDENTIALS: "oauth2_client_credentials",
} as const;

// ─── Access management ───────────────────────────────────────────

/**
 * API permission codes, as they appear in the logged-in user's `access.permissions`.
 *
 * Mirrors `Permission` in olive-backend `src/app/security/permissions.py` 1:1 and in the same
 * order, so the two flat lists can be diffed by eye when either side changes.
 */
export const PERMISSION = {
  USER_VIEW: "user-view",
  USER_ADD: "user-add",
  USER_UPDATE: "user-update",
  USER_DELETE: "user-delete",
  CONNECTION_VIEW: "connection-view",
  CONNECTION_DETAIL_VIEW: "connection-detail-view",
  CONNECTION_ADD: "connection-add",
  CONNECTION_DELETE: "connection-delete",
  DOMAIN_VIEW: "domain-view",
  DOMAIN_ADD: "domain-add",
  DOMAIN_UPDATE: "domain-update",
  NUGGET_CONFIG_VIEW: "nugget-config-view",
  NUGGET_CONFIG_UPDATE: "nugget-config-update",
  DOMAIN_DOCUMENT_VIEW: "domain-document-view",
  DOMAIN_DOCUMENT_UPDATE: "domain-document-update",
  DOMAIN_OVERVIEW_VIEW: "domain-overview-view",
  DOMAIN_OVERVIEW_UPDATE: "domain-overview-update",
  SCHEDULED_JOB_VIEW: "scheduled-job-view",
  SCHEDULED_JOB_MANAGE: "scheduled-job-manage",
  ALATION_MANAGE: "alation-manage",
  ALATION_CHAT: "alation-chat",
  SSO_VIEW: "sso-view",
  SSO_ADD: "sso-add",
  SSO_UPDATE: "sso-update",
  BUSINESS_CONTEXT_VIEW: "business-context-view",
  BUSINESS_CONTEXT_UPDATE: "business-context-update",
  LLM_KEY_VIEW: "llm-key-view",
  LLM_KEY_ADD: "llm-key-add",
  LLM_KEY_UPDATE: "llm-key-update",
  LLM_KEY_DELETE: "llm-key-delete",
  TOKEN_USAGE_VIEW: "token-usage-view",
  LOG_VIEW: "log-view",
  /** Settings > Branding (logo and product name). Unreleased: Super Administrator only. */
  BRANDING_MANAGE: "branding-manage",
  APP_VIEW: "app-view",
  APP_ADD: "app-add",
  APP_UPDATE: "app-update",
  APP_DELETE: "app-delete",
  APP_CHART_VIEW: "app-chart-view",
  APP_CHART_ADD: "app-chart-add",
  APP_CHART_UPDATE: "app-chart-update",
  APP_CHART_DELETE: "app-chart-delete",
  APP_TABLE_VIEW: "app-table-view",
  APP_TABLE_ADD: "app-table-add",
  APP_TABLE_UPDATE: "app-table-update",
  APP_TABLE_DELETE: "app-table-delete",
  APP_TABLE_APPROVE: "app-table-approve",
  APP_KPI_VIEW: "app-kpi-view",
  APP_KPI_ADD: "app-kpi-add",
  APP_KPI_UPDATE: "app-kpi-update",
  APP_KPI_DELETE: "app-kpi-delete",
  APP_QUERY_VIEW: "app-query-view",
  APP_QUERY_ADD: "app-query-add",
  APP_QUERY_UPDATE: "app-query-update",
  APP_QUERY_DELETE: "app-query-delete",
  APP_TAB_VIEW: "app-tab-view",
  APP_TAB_ADD: "app-tab-add",
  APP_TAB_UPDATE: "app-tab-update",
  APP_TAB_DELETE: "app-tab-delete",
  APP_CATEGORY_VIEW: "app-category-view",
  /** App Studio: download an app as a JSON file. Unreleased: Super Administrator only. */
  APP_EXPORT: "app-export",
  /** App Studio: create an app from an exported JSON file. Unreleased: Super Administrator only. */
  APP_IMPORT: "app-import",
  PUBLISHED_APP_VIEW: "published-app-view",
  CHAT_VIEW: "chat-view",
  CHAT_ADD: "chat-add",
  CHAT_UPDATE: "chat-update",
  CHAT_DELETE: "chat-delete",
  /** Each LLM and tool call's timing in a chat's time breakdown. Unreleased: Super Administrator only. */
  CHAT_CALL_TIMING_VIEW: "chat-call-timing-view",
  NUGGET_VIEW: "nugget-view",
  NUGGET_ADD: "nugget-add",
  NUGGET_UPDATE: "nugget-update",
  NUGGET_DELETE: "nugget-delete",
  NUGGET_GENERATE: "nugget-generate",
  NUGGET_ACTION_EXECUTE: "nugget-action-execute",
  INTEGRATION_VIEW: "integration-view",
  INTEGRATION_ADD: "integration-add",
  ROLE_VIEW: "role-view",
  ROLE_ADD: "role-add",
  ROLE_UPDATE: "role-update",
  ACCESS_VIEW: "access-view",
  ACCESS_ADD: "access-add",
  ACCESS_DELETE: "access-delete",
  DOMAIN_ACCESS_VIEW: "domain-access-view",
  DOMAIN_ACCESS_ADD: "domain-access-add",
  DOMAIN_ACCESS_DELETE: "domain-access-delete",
  NUGGET_ACTION_VIEW: "nugget-action-view",
  CONTEXT_HUB_VIEW: "context-hub-view",
  CONTEXT_HUB_UPDATE: "context-hub-update",
  OWN_CACHE_CLEAR: "own-cache-clear",
  USER_CACHE_CLEAR: "user-cache-clear",
} as const;

export type PermissionCode = (typeof PERMISSION)[keyof typeof PERMISSION];

/**
 * The built-in role's name. It is not a `roles` row at all — the capability is a column on the
 * user and its permissions come from backend code — so the Roles screen never lists it, and the
 * backend rejects creating or renaming a role to this name.
 */
export const SUPER_ADMINISTRATOR_ROLE_NAME = "Super Administrator";

/**
 * Which grant level a role belongs to. A `tenant` role is assigned on the Users screen and confers
 * tenant-wide permissions (SSO, LLM keys, Business Context, user management); a `domain` role is
 * assigned per domain from its Access tab. The two permission sets are disjoint, so a tenant role
 * never confers access to any domain's data.
 */
export const ROLE_SCOPE = {
  TENANT: "tenant",
  DOMAIN: "domain",
} as const;

export type RoleScope = (typeof ROLE_SCOPE)[keyof typeof ROLE_SCOPE];

// ─── Actions Hub ─────────────────────────────────────────────────

/** Actions Hub integration type values (API wire format). Slack and Email today; more can be added later. */
export const INTEGRATION_TYPE = {
  SLACK: "SLACK",
  EMAIL: "EMAIL",
} as const;

/** DNS record type values returned for an Email integration's SES identity (API wire format). */
export const INTEGRATION_DNS_RECORD_TYPE = {
  CNAME: "CNAME",
  MX: "MX",
  TXT: "TXT",
} as const;

/** Verification status of an Email integration's DKIM / MAIL FROM domain (API wire format,
 * mirrors AWS SES VerificationStatus values). */
export const EMAIL_VERIFICATION_STATUS = {
  PENDING: "PENDING",
  SUCCESS: "SUCCESS",
  FAILED: "FAILED",
  TEMPORARY_FAILURE: "TEMPORARY_FAILURE",
  NOT_STARTED: "NOT_STARTED",
} as const;

// ─── Learning History ────────────────────────────────────────────

/** How a Learning History insight participates in chat (API wire format). */
export const LEARNING_INSIGHT_APPLICATION_MODE = {
  APPLY_BASED_ON_CONFIDENCE: "apply_based_on_confidence",
  IGNORED: "ignored",
  TEMPORARILY_IGNORED: "temporarily_ignored",
  ALWAYS_APPLY: "always_apply",
  MANUAL_PRIORITY: "manual_priority",
} as const;

/** Confidence/priority label values (API wire format) - shared by confidence_label and
 * manual_priority_label. */
export const LEARNING_INSIGHT_PRIORITY_LABEL = {
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low",
} as const;

/** Review status of a domain learning (API wire format). Only approved ones reach chat. */
export const DOMAIN_LEARNING_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
} as const;

/** The user's answer to "save this as your learning?" under a chat answer (API wire format). */
export const LEARNING_SUGGESTION_DECISION = {
  SAVED: "saved",
  DISMISSED: "dismissed",
} as const;

/** The user's answer to "suggest it for the whole domain?", asked once saved (API wire format). */
export const LEARNING_SUGGESTION_DOMAIN_DECISION = {
  SUGGESTED: "suggested",
  KEPT_PERSONAL: "kept_personal",
} as const;

/** What the chat answer's learning prompt sends back (API wire format). */
export const LEARNING_SUGGESTION_CHOICE = {
  SAVE: "save",
  DISMISS: "dismiss",
  SUGGEST_TO_DOMAIN: "suggest_to_domain",
  KEEP_PERSONAL: "keep_personal",
} as const;

/** Where a term meaning applied in chat came from (API wire format, `scope` on a resolution). */
export const LEARNING_RESOLUTION_SCOPE = {
  USER: "user",
  DOMAIN: "domain",
} as const;

/** Most ranking hints ("From your Learning History", …) shown under one clarification option. */
export const CLARIFICATION_OPTION_MAX_HINTS = 2;

/** Nugget action outcome status values (API wire format; distinct from the async STATUS enum). */
export const ACTION_STATUS = {
  PENDING: "PENDING",
  SUCCESS: "SUCCESS",
  FAILED: "FAILED",
} as const;

/** How often the Actions page polls for nugget-action status updates while pending. */
export const NUGGET_ACTIONS_POLL_INTERVAL_MS = 120_000;


/**
 * Nugget severity stored in `nuggets.details.severity` (API wire format, lowercase).
 * Deliberately separate from `LEARNING_INSIGHT_PRIORITY_LABEL` despite the identical values: that is
 * a confidence scale where high is good, this is a risk scale where high is bad, so the two cannot
 * share a color map.
 */
export const NUGGET_SEVERITY = {
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low",
} as const;

export type NuggetSeverity =
  (typeof NUGGET_SEVERITY)[keyof typeof NUGGET_SEVERITY];

// ─── App Studio ──────────────────────────────────────────────────

/** Stack group the chart-config editor gives every bar measure the user marks "Stack". */
export const APP_STUDIO_CHART_STACK_GROUP = "stack";

/**
 * Ceiling on the level combinations one widget's taxonomy may carry. Past this the client stops
 * offering editing rather than freezing the tab building option indexes.
 */
export const APPROVAL_TAXONOMY_MAX_ENTRIES = 50_000;

/** localStorage key prefix for a viewer's unapproved taxonomy edits: `{prefix}:{appId}:{tableId}`. */
export const APP_TABLE_APPROVAL_DRAFTS_STORAGE_PREFIX =
  "olive_app_table_approval_drafts";

/**
 * localStorage key prefix for a viewer's own column sort of a published table:
 * `{prefix}:user-{userId}:{appId}:{tableId}`, or `{prefix}:share-{code}:{tableId}` on a share link.
 */
export const APP_TABLE_VIEWER_SORT_STORAGE_PREFIX = "olive_app_table_sort";

/**
 * localStorage key prefix for a viewer's breakdown picks (breakdown id → option key):
 * `{prefix}:user-{userId}:{appId}`, or `{prefix}:share-{code}` on a share link.
 */
export const APP_BREAKDOWN_SELECTIONS_STORAGE_PREFIX = "olive_app_breakdown_selections";

/**
 * Whether tab-level breakdowns are offered: the tab's breakdown bar and the "A tab breakdown"
 * choice when setting one up on a widget. Off for now, so every breakdown belongs to one widget.
 * The API still serves tab breakdowns; while this is off, any that exist switch from the header
 * of each widget they drive instead of from a bar.
 */
export const APP_TAB_BREAKDOWNS_ENABLED = false;

/** Bounds on a breakdown's option list — the API refuses anything outside them. */
export const BREAKDOWN_MIN_OPTIONS = 2;
export const BREAKDOWN_MAX_OPTIONS = 12;
export const BREAKDOWN_NAME_MAX_LENGTH = 120;
export const BREAKDOWN_OPTION_LABEL_MAX_LENGTH = 60;
export const BREAKDOWN_DESCRIPTION_MAX_LENGTH = 1000;
/** An option key is `^[a-z0-9_]{1,64}$`; keys the client derives are cut to this length. */
export const BREAKDOWN_OPTION_KEY_MAX_LENGTH = 64;

/**
 * A widget's own switcher renders as a segmented control while every option fits on one short
 * row, and as a compact select once there are more options or longer labels than that.
 */
export const BREAKDOWN_SEGMENTED_MAX_OPTIONS = 4;
export const BREAKDOWN_SEGMENTED_MAX_LABEL_LENGTH = 10;

/** Width of a tab-level breakdown's select in the breakdown bar. */
export const BREAKDOWN_TAB_SWITCHER_MIN_WIDTH_PX = 140;

/** How long an unapproved local edit is kept before it is treated as abandoned. */
export const APP_TABLE_APPROVAL_DRAFT_TTL_MS = 14 * 24 * 60 * 60 * 1000;

/** Width of the column holding a row's discard control, immediately after the checkbox. Sized to the control itself — a 20px icon plus its 4px padding — so it takes as little of the row as it can. */
export const APP_TABLE_ROW_STATUS_WIDTH_PX = 28;

/**
 * Width of a taxonomy dropdown cell in a paginated grid. Such a cell opts out of the ellipsis
 * clamp, which sets `max-width: 0` and would collapse the control, so it needs a bound of its own.
 */
export const APP_TABLE_TAXONOMY_CELL_WIDTH_PX = 180;

/**
 * Width of the row-selection checkbox column in an editable grid. Narrow on purpose: it holds one
 * control and, being pinned while the rest of the table scrolls, every pixel it takes is a pixel
 * that never scrolls away.
 */
export const APP_TABLE_ROW_SELECT_WIDTH_PX = 44;

/**
 * Width of a read-only cell in an editable grid, which uses a fixed table layout. Fixed layout
 * ignores content, so without an explicit width these columns would split whatever the dropdowns
 * left over -- which, next to a 180px editor, is close to nothing.
 */
export const APP_TABLE_EDITING_TEXT_CELL_WIDTH_PX = 140;

/**
 * Narrowest a read-only data column is allowed to be. Below this, a value of a few characters
 * is no longer readable, so a wide table scrolls sideways instead of squeezing further.
 */
export const APP_TABLE_COLUMN_MIN_WIDTH_PX = 120;

/**
 * Bound used only before the card width has been measured. Once it has, columns share the card
 * and fill it, so a single column takes the whole width. This cap only stops a long value from
 * painting at an unbounded width on that first, unmeasured layout.
 */
export const APP_TABLE_COLUMN_MAX_WIDTH_PX = 960;

/**
 * Width of an editable field in the single-record form, and of one in a drill-down dialog's
 * header. Bounded rather than fluid: the form's other fields are plain text that ends where its
 * value does, so an input whose underline ran the width of the dialog would be the only thing on
 * the page drawing a line across it.
 */
export const APP_TABLE_FORM_FIELD_WIDTH_PX = 280;

/**
 * Lines of text a table cell shows before it is cut off, on a surface whose rows are free to grow
 * (the expanded view, the edit preview, a proposal card). A paginated grid is not one of them: its
 * rows are pinned to APP_TABLE_ROW_HEIGHT_PX so exactly APP_TABLE_PAGE_SIZE of them fit the panel,
 * and it stays at one line.
 *
 * Three rather than unbounded because unbounded is what these surfaces do today: one paragraph-long
 * value makes its row taller than the dozen rows around it, and the table stops being scannable.
 */
export const APP_TABLE_CELL_MAX_LINES = 3;

/**
 * How long a cell's text has to be before the cell is treated as cut off -- which is what turns on
 * the pointer cursor and lets a click open the full value.
 *
 * Per line, so a one-line grid uses this and an APP_TABLE_CELL_MAX_LINES surface uses this times
 * that. Judged from the text rather than measured from the DOM on purpose: measuring means a ref
 * and a layout read per cell, on every render, for a grid that already renders hundreds of them --
 * and the cost of being wrong is trivial either way. A short value in a narrow column keeps its
 * tooltip and its ellipsis, it just is not clickable; a long value in a wide column is clickable
 * when it did not need to be, and opens a dialog showing what was already on screen.
 */
export const APP_TABLE_CELL_CLIPPED_CHARS_PER_LINE = 40;

/** Width of the dialog that shows one table cell's full value (px). Wide enough for a sentence to break naturally, narrow enough to stay beside the cell it was opened from rather than over the table. */
export const APP_TABLE_CELL_DIALOG_MAX_WIDTH_PX = 420;

/** Height that dialog scrolls past (px). A cell can hold a whole document; without a cap the dialog would run off the screen and take its own scrollbar out of reach. */
export const APP_TABLE_CELL_DIALOG_MAX_HEIGHT_PX = 320;

/**
 * Height the export dialog's column checklist scrolls past (px). A wide table can carry dozens of
 * columns; without a cap the list pushes the dialog's own footer off the screen.
 */
export const APP_TABLE_EXPORT_COLUMN_LIST_MAX_HEIGHT_PX = 280;

/**
 * Byte-order mark prepended to a downloaded CSV. Written as an escape rather than pasted, because
 * U+FEFF renders as nothing — pasted, this constant silently becomes an empty string and the bug
 * only shows up as mangled accents in Excel on Windows, which reads a BOM-less file as ANSI.
 */
export const CSV_UTF8_BOM = "\uFEFF";

/** Input-mode values for the Add/Edit chart, table, and KPI card dialogs (how the widget's SQL is authored). */
export const APP_WIDGET_INPUT_MODE = {
  QUERY: "query",
  FORM: "form",
  PROMPT: "prompt",
  /** Rows come from JSON files under the domain's `inputs/` prefix instead of from SQL. */
  S3: "s3",
  /** Rows join two saved queries -- one with its previous period, or with another query. */
  COMPARISON: "comparison",
} as const;

/**
 * The Add widgets dialog's "Saved query" choice, listed with the input modes in one dropdown. Not
 * an input mode itself: it reuses a query already saved, whatever mode authored it.
 */
export const SAVED_QUERY_SOURCE = "saved_query";

/** What a comparison's current side is compared with. Mirrors the backend's `compare.kind`. */
export const QUERY_COMPARISON_KIND = {
  /** The same query for the period before, following the tab's date filter. */
  PREVIOUS_PERIOD: "previous_period",
  /** The same query for the same dates one year earlier. */
  PREVIOUS_YEAR: "previous_year",
  /** Any other SQL-backed saved query. */
  QUERY: "query",
  /** No second side: formula columns worked out from the current query's own columns. */
  SELF: "self",
} as const;

/** Fewest columns a query needs before its own columns can be worked on: two, to relate. */
export const QUERY_COMPARISON_OWN_COLUMNS_MIN = 2;

/** Which rows a comparison keeps, in SQL join terms. */
export const QUERY_COMPARISON_JOIN = {
  /** Every row of the current side; the other side's values are empty where nothing matched. */
  LEFT: "left",
  INNER: "inner",
  FULL: "full",
} as const;

/** The side a formula reads a column from: `current.X`, or `previous.X` / `compare.X`. */
export const QUERY_COMPARISON_ALIAS = {
  CURRENT: "current",
  PREVIOUS: "previous",
  COMPARE: "compare",
} as const;

/**
 * Suffix the other side's columns take in a comparison's output (`REVENUE_PREVIOUS`): `previous`
 * for the period kinds, `compare` for another query. Upper-cased for an upper-case column.
 */
export const QUERY_COMPARISON_SUFFIX = {
  PREVIOUS: "previous",
  COMPARE: "compare",
} as const;

/** The input modes a comparison side may have: SQL-backed ones. No files, no nesting. */
export const QUERY_COMPARISON_SIDE_INPUT_MODES = [
  APP_WIDGET_INPUT_MODE.QUERY,
  APP_WIDGET_INPUT_MODE.FORM,
  APP_WIDGET_INPUT_MODE.PROMPT,
] as const;

/** Most column pairs a comparison matches rows on. */
export const QUERY_COMPARISON_MAX_MATCH_COLUMNS = 5;

/** Most formula columns one comparison may add. */
export const QUERY_COMPARISON_MAX_FORMULAS = 20;

/** Longest formula expression accepted. */
export const QUERY_COMPARISON_FORMULA_MAX_LENGTH = 1000;

/** Longest formula column name accepted. */
export const QUERY_COMPARISON_FORMULA_NAME_MAX_LENGTH = 64;

/** A formula column's name: a plain identifier, so a widget can bind to it like any column. */
export const QUERY_COMPARISON_FORMULA_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;

/** Most "% change" formulas the one-click comparison suggests, so a wide query stays readable. */
export const QUERY_COMPARISON_SUGGESTED_FORMULA_MAX = 3;

/** How tall a comparison's read-only SQL (its current query, each executed side) grows. */
export const QUERY_COMPARISON_SQL_MAX_HEIGHT_PX = 220;

/** An integer column named like this identifies a row (a year, an id), so it is matched on. */
export const QUERY_COMPARISON_KEY_COLUMN_PATTERN =
  /(^|_)(id|key|code|year|month|week|day|date|num)(_|$)/i;

/**
 * A column named like this holds a moment (`call_start_time`, `created_at`), which never repeats
 * from one period to the next, so it is not suggested as a match column.
 */
export const QUERY_COMPARISON_TIME_COLUMN_PATTERN = /(^|_)(time|timestamp|ts|datetime|at)$/i;

/** How many of a column's values are read to tell whether it holds times of day. */
export const QUERY_COMPARISON_TIME_SAMPLE_ROWS = 20;

/** Suffix of a suggested "% change" formula column: `revenue_change_pct`. */
export const QUERY_COMPARISON_CHANGE_PCT_SUFFIX = "change_pct";

/**
 * How a widget drafted on a comparison first shows a suggested "% change" formula column: a
 * table column or chart measure with this symbol and these decimals (`12.5 %`), a KPI card with
 * this card format. Only a seed -- the widget's Format section changes it like any other.
 */
export const QUERY_COMPARISON_CHANGE_PCT_FORMAT = {
  SYMBOL: "%",
  DECIMAL_PLACES: 1,
  KPI_FORMAT: "percent",
} as const;

// ─── Value formatting ────────────────────────────────────────────

/** What a column's display format does with its values: numbers, or dates and times. */
export const VALUE_FORMAT_TYPE = {
  NUMBER: "number",
  DATE: "date",
} as const;

/** Which side of a number its symbol sits on. */
export const VALUE_FORMAT_SYMBOL_POSITION = {
  PREFIX: "prefix",
  SUFFIX: "suffix",
} as const;

/**
 * The symbols a number format can add, each with the side it sits on unless the author picks the
 * other. To offer another symbol, add one entry here and the same symbol to the backend's
 * `VALUE_FORMAT_SYMBOLS` (`olive-backend/src/app/schemas/value_format.py`), which validates it.
 */
export const VALUE_FORMAT_SYMBOLS = [
  { symbol: "$", defaultPosition: VALUE_FORMAT_SYMBOL_POSITION.PREFIX },
  { symbol: "%", defaultPosition: VALUE_FORMAT_SYMBOL_POSITION.SUFFIX },
] as const;

/**
 * What separates a number format's symbol from the value: `$ 1,650`, `45 %`. A narrow no-break
 * space -- a slight gap that also keeps a wrapping table cell from putting the symbol on a line
 * of its own.
 */
export const VALUE_FORMAT_SYMBOL_SPACING = "\u202F";

/** Most digits a number format may fix after the decimal point. */
export const VALUE_FORMAT_MAX_DECIMAL_PLACES = 6;

/**
 * Locale numbers and month names are written in. Fixed rather than the viewer's own so every
 * viewer of an app reads the same `1,650` and `Jan 5, 2024` the author configured.
 */
export const VALUE_FORMAT_LOCALE = "en-US";

/** Longest date pattern accepted, input or output. */
export const VALUE_FORMAT_DATE_PATTERN_MAX_LENGTH = 64;

/** Input patterns that read a number as Unix time rather than as calendar text. */
export const VALUE_FORMAT_EPOCH_INPUT_PATTERN = {
  SECONDS: "X",
  MILLISECONDS: "x",
} as const;

/**
 * Display patterns offered in the date format picker; any other pattern can be typed as a custom
 * one. Tokens: YYYY/YY year, MMMM/MMM/MM/M month, DD/D day, dddd/ddd weekday, HH/H 24-hour,
 * hh/h 12-hour, mm minute, ss second, SSS millisecond, A/a AM-PM; [text] is printed as is.
 */
export const VALUE_FORMAT_DATE_OUTPUT_PATTERNS = [
  "MMM D, YYYY",
  "MMMM D, YYYY",
  "D MMM YYYY",
  "YYYY-MM-DD",
  "MM/DD/YYYY",
  "DD/MM/YYYY",
  "MMM YYYY",
  "ddd, MMM D, YYYY",
  "YYYY-MM-DD HH:mm",
  "MMM D, YYYY h:mm A",
  "MM/DD/YYYY hh:mm:ss A",
  "HH:mm:ss",
] as const;

/**
 * How incoming text can be read, offered beside "Automatic" (ISO dates and timestamps, which is
 * how the warehouse's date and timestamp columns arrive). Any other pattern can be typed.
 */
export const VALUE_FORMAT_DATE_INPUT_PATTERNS = [
  "DD/MM/YYYY",
  "MM/DD/YYYY",
  "DD-MM-YYYY",
  "YYYYMMDD",
  "DD/MM/YYYY HH:mm",
  "MM/DD/YYYY hh:mm A",
  "MMM D, YYYY",
  VALUE_FORMAT_EPOCH_INPUT_PATTERN.SECONDS,
  VALUE_FORMAT_EPOCH_INPUT_PATTERN.MILLISECONDS,
] as const;

/** Display pattern a column switched to a date format starts with. */
export const VALUE_FORMAT_DEFAULT_DATE_OUTPUT_PATTERN = "MMM D, YYYY";

/**
 * How a comparison's periods are dated on a widget: `vs Oct 25 – Oct 31`. The year is added only
 * when the two periods fall in different years, where "same period last year" would otherwise
 * print the very dates of this one.
 */
export const COMPARISON_PERIOD_DATE_PATTERN = {
  SAME_YEAR: "MMM D",
  WITH_YEAR: "MMM D, YYYY",
} as const;

// ─── Published apps ──────────────────────────────────────────────

/**
 * How long a favourite app's prefetched view data stays replayable. This is a prefetch window
 * rather than a cache: past it, opening the app streams live data exactly as it always has.
 */
export const PUBLISHED_APP_PREFETCH_TTL_MS = 5 * 60 * 1000;

/**
 * Ceiling on how many favourites are warmed after sign-in. Each one runs every widget query on the
 * server, so a viewer who has favourited dozens would otherwise turn signing in into a stampede.
 */
export const PUBLISHED_APP_PREFETCH_MAX_APPS = 5;

/**
 * Delay before the favourites prefetch starts, so the requests the landing page actually renders
 * from — the logged-in user, tenant entitlements, the app list itself — are not queued behind it.
 */
export const PUBLISHED_APP_PREFETCH_START_DELAY_MS = 1_500;

// ─── Tenants ─────────────────────────────────────────────────────

/** Tenant provisioning status values from the Olive API. */
export const TENANT_STATUS = {
  PENDING: "pending",
  PROVISIONING: "provisioning",
  ACTIVE: "active",
  FAILED: "failed",
} as const;

/**
 * Supported range and default for a tenant's query-cache clear interval, in minutes.
 * Mirrors the backend `app/cache/query_cache_schedule.py` bounds — the API rejects
 * anything outside MIN..MAX.
 *
 * Minutes rather than hours because staff enter the interval as free-form hours and
 * minutes (00h 10m), so the wire value has to be able to carry a sub-hour interval.
 * MIN is 5 rather than 1 because the backend memoizes the interval and sweeps for
 * expired boundaries on 60-second cycles.
 */
export const QUERY_CACHE_CLEAR_INTERVAL_MINUTES = {
  MIN: 5,
  MAX: 24 * 60,
  DEFAULT: 4 * 60,
} as const;

/** Bounds for the two entry fields the clear interval is composed from. */
export const QUERY_CACHE_CLEAR_INTERVAL_FIELDS = {
  MAX_HOURS: 24,
  MAX_MINUTES: 59,
} as const;

// ─── Chat ────────────────────────────────────────────────────────

/** Seconds into a running chat turn after which the processing panel adds reassurance copy. */
export const CHAT_TURN_REASSURANCE_AFTER_SECONDS = 45;

/** Rows per page in a chat answer's result table (paged in the browser). */
export const CHAT_TABLE_ROWS_PER_PAGE = 10;

/**
 * Two timed steps of a chat turn count as running in parallel only when they overlap by more
 * than this, so steps that merely hand off to each other -- one ends as the next starts, give
 * or take a database write -- are not reported as parallel.
 */
export const CHAT_TIMING_PARALLEL_MIN_OVERLAP_MS = 50;
